import type {
  RuleSet,
  StoredRuleSet,
  Session,
  QuestionNode,
  ResultNode,
  Answer,
  AnswerRecord,
  EvaluateCondition,
  GateNode,
} from "./types";
import { hydrateRuleSet } from "./hydrate";
import { getComputedValue, setComputedValue } from "./computed";
import { resolve as defaultResolve } from "../i18n/catalogs";
import type { ResolveContext } from "../i18n/types";

const DEFAULT_CONTEXT: ResolveContext = { locale: "en", audience: "family" };

function evalGate(session: Session, gate: GateNode): string {
  const v = getComputedValue(session, gate.computed);
  for (const r of gate.rules) {
    let hit = false;
    switch (r.op) {
      case ">=": hit = v >= r.value; break;
      case ">": hit = v > r.value; break;
      case "<=": hit = v <= r.value; break;
      case "<": hit = v < r.value; break;
      case "==": hit = v === r.value; break;
      default: throw new Error(`Unknown gate operator "${(r as { op: string }).op}"`);
    }
    if (hit) return r.goto;
  }
  return gate.default;
}

// Advance past any gate node (evaluating its computed value) and any node not
// applicable to the audience (following its skipTo edge).
function advanceToInteractiveNode(session: Session, nodeId: string): string {
  const seen = new Set<string>();
  let current = nodeId;
  while (true) {
    const node = session.rules.nodes[current];
    if (!node) return current;
    if (node.type === "gate") {
      if (seen.has(current)) throw new Error(`Cycle at gate "${current}"`);
      seen.add(current);
      current = evalGate(session, node);
      continue;
    }
    if (node.audiences && !node.audiences.includes(session.audience)) {
      if (node.skipTo === undefined) {
        throw new Error(
          `Node "${current}" is gated to [${node.audiences.join(", ")}] and has no skipTo for audience "${session.audience}"`
        );
      }
      if (seen.has(current)) throw new Error(`Cycle while skipping audience-scoped nodes at "${current}"`);
      seen.add(current);
      current = node.skipTo;
      continue;
    }
    return current;
  }
}

export function createSession(
  rules: StoredRuleSet | RuleSet,
  context: ResolveContext = DEFAULT_CONTEXT
): Session {
  const hydrated = hydrateRuleSet(rules, defaultResolve, context);
  const session: Session = {
    rules: hydrated,
    storedRules: rules,
    audience: context.audience,
    locale: context.locale,
    currentNodeId: hydrated.root,
    history: [],
  };
  return { ...session, currentNodeId: advanceToInteractiveNode(session, hydrated.root) };
}

export function getCurrentQuestion(session: Session): QuestionNode | null {
  const node = session.rules.nodes[session.currentNodeId];
  if (node.type === "question") return node;
  return null;
}

export function getResult(session: Session): ResultNode | null {
  const node = session.rules.nodes[session.currentNodeId];
  if (node.type === "result") return node;
  return null;
}

export function getHistory(session: Session): AnswerRecord[] {
  return session.history;
}

export function goBack(session: Session): Session {
  if (session.history.length === 0) return session;
  const newHistory = session.history.slice(0, -1);
  const previous = session.history[session.history.length - 1];
  return {
    ...session,
    currentNodeId: previous.nodeId,
    history: newHistory,
  };
}

function evaluateCondition(
  condition: EvaluateCondition["condition"],
  selected: string[],
  totalOptions: number
): boolean {
  if (condition === "any") return selected.length > 0;
  if (condition === "all") return selected.length === totalOptions;
  if (typeof condition === "object" && "atLeast" in condition) {
    return selected.length >= condition.atLeast;
  }
  return false;
}

function resolveNextNode(node: QuestionNode, answer: Answer): string {
  const next = node.next;
  if ("_evaluate" in next) {
    const ev = (next as { _evaluate: EvaluateCondition })._evaluate;
    const selected = Array.isArray(answer) ? answer : [];
    const totalOptions = node.options?.length ?? 0;
    const matches = evaluateCondition(ev.condition, selected, totalOptions);
    return matches ? ev.match : ev.noMatch;
  }
  const directNext = next as Record<string, string>;
  return directNext[String(answer)];
}

export function submitAnswer(session: Session, answer: Answer): Session {
  const currentNode = session.rules.nodes[session.currentNodeId];
  if (currentNode.type !== "question") {
    throw new Error(`Cannot submit answer: current node ${session.currentNodeId} is not a question`);
  }

  const nextNodeId = resolveNextNode(currentNode, answer);
  const record: AnswerRecord = {
    nodeId: session.currentNodeId,
    question: currentNode,
    answer,
  };
  let advanced: Session = { ...session, currentNodeId: nextNodeId, history: [...session.history, record] };
  if (currentNode.type === "question" && currentNode.setsComputed) {
    const opt = currentNode.options?.find((o) => o.value === String(answer));
    if (opt?.score !== undefined) {
      advanced = setComputedValue(advanced, currentNode.setsComputed, opt.score);
    }
  }
  return { ...advanced, currentNodeId: advanceToInteractiveNode(advanced, nextNodeId) };
}
